import React, { FunctionComponent, useState } from "react"
import { useTranslation } from "react-i18next"

import { Grid, GridItem } from "components/Grid"
import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import Select from "components/Select/Select"
import SelectSupplier from "components/Select/SelectSupplier"
import Input from "components/Input/Input"
import InputNumber from "components/InputNumber/InputNumber"
import InputDateTime from "components/InputDateTime/InputDateTime"
import FormField from "components/FormField/FormField"
import AssetStatusTag from "components/AssetStatusTag/AssetStatusTag"
import { AssetOwnershipType, getAssetStatusLabel } from "models/Asset"
import { targetStatuses } from "./copyAssetMatrix"
import {
    ICopyAssetSharedValues,
    ICopyAssetState,
    CopyAssetSharedKey,
    needsOwnerInput,
    overrideCount,
} from "./copyAssetRows"
import { COPY_ASSET_TEXT_FIELDS, ownershipAmountField } from "./copyAssetFieldDefs"
import CopyAssetNotesField from "./CopyAssetNotesField"

export interface ICopyAssetSharedDefaultsProps {
    state: ICopyAssetState
    editedCount: number
    /** Currency shown after the amount fields, keyed by shared value. */
    amountCurrencies: Partial<Record<CopyAssetSharedKey, string>>
    onSetShared<K extends CopyAssetSharedKey>(key: K, value: ICopyAssetSharedValues[K]): void
}

/**
 * The "applies to all copies" panel. Every value entered here is inherited by
 * each copy that has no own value for the field; a marker above a field shows
 * how many copies override it. Validation happens on the copies only — the
 * panel itself never flags its values.
 */
const CopyAssetSharedDefaults: FunctionComponent<React.PropsWithChildren<ICopyAssetSharedDefaultsProps>> = ({
    state,
    editedCount,
    amountCurrencies,
    onSetShared,
}) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(true)

    const isBorrowed = state.ownershipType === AssetOwnershipType.borrowed
    const textFields = [...COPY_ASSET_TEXT_FIELDS, ownershipAmountField(isBorrowed)]

    function ownValuesNote(count: number) {
        if (count === 0) return undefined
        return (
            <Text type="pSmallest" className="copy-asset__own-note">
                {t('copyAsset.ownValues', { count })}
            </Text>
        )
    }

    return (
        <div className="copy-asset__defaults bg-grey-50 round p-2 mb-3">
            <Flex justifyContent="space-between" alignItems="center" gap="1.6rem">
                <Flex alignItems="baseline" gap="1.2rem">
                    <Text type="h5">{t('copyAsset.appliesToAllCopies')}</Text>
                    <Text type="pSmallest" className="color-grey-500">
                        {t('copyAsset.appliesSubline')}
                        {editedCount > 0 && ` · ${t('copyAsset.editedIndividually', { edited: editedCount, total: state.rows.length })}`}
                    </Text>
                </Flex>
                <Button
                    theme="link"
                    label={isOpen ? t('copyAsset.hide') : t('copyAsset.show')}
                    onClick={() => setIsOpen(open => !open)}
                />
            </Flex>
            {isOpen &&
                <Grid columns="repeat(auto-fill, minmax(18rem, 1fr))" gap="0.8rem 1.6rem" className="mt-1">
                    <GridItem className="copy-asset__field">
                        {ownValuesNote(overrideCount(state, "status"))}
                        <FormField label={t('asset.assetStatus')}>
                            <Select
                                value={state.shared.status}
                                options={targetStatuses(state.ownershipType).map(status => ({
                                    label: getAssetStatusLabel(status),
                                    value: status,
                                }))}
                                optionTemplate={option => <AssetStatusTag status={option.value} />}
                                optionTemplateContext="all"
                                onChange={option => option && onSetShared("status", option.value)}
                                isInModal
                                testIdPrefix="copy-asset-shared-status"
                            />
                        </FormField>
                    </GridItem>
                    {needsOwnerInput(state) &&
                        <GridItem className="copy-asset__field">
                            {ownValuesNote(overrideCount(state, "ownerName"))}
                            <FormField label={t('copyAsset.fields.companyWhereBorrowedAsset')}>
                                <SelectSupplier
                                    value={state.shared.ownerName}
                                    isInModal
                                    testIdPrefix="copy-asset-shared-ownerName"
                                    onChange={ownerName => onSetShared("ownerName", ownerName ?? "")}
                                />
                            </FormField>
                        </GridItem>
                    }
                    {textFields.map(field => {
                        const ownValues = overrideCount(state, field.key)
                        if (field.key === "internalNotes") {
                            return (
                                <GridItem key={field.key} className="copy-asset__field">
                                    {ownValuesNote(ownValues)}
                                    <CopyAssetNotesField
                                        label={t(field.labelKey)}
                                        value={state.shared.internalNotes}
                                        onChange={notes => onSetShared("internalNotes", notes)}
                                        testId="copy-asset-shared-internalNotes"
                                    />
                                </GridItem>
                            )
                        }
                        if (field.isAmount) {
                            return (
                                <GridItem key={field.key} className="copy-asset__field">
                                    {ownValuesNote(ownValues)}
                                    <FormField label={t(field.labelKey)}>
                                        <InputNumber
                                            value={state.shared[field.key]}
                                            minDecimals={2}
                                            maxDecimals={2}
                                            placeholder={t('common.pleaseEnter')}
                                            after={<Text className="px-2">{amountCurrencies[field.key]}</Text>}
                                            onChange={value => onSetShared(field.key, value)}
                                            data-testid={`copy-asset-shared-${field.key}`}
                                        />
                                    </FormField>
                                </GridItem>
                            )
                        }
                        return (
                            <GridItem key={field.key} className="copy-asset__field">
                                {ownValuesNote(ownValues)}
                                <FormField label={t(field.labelKey)}>
                                    <Input
                                        value={state.shared[field.key]}
                                        maxLength={field.maxLength}
                                        placeholder={t('common.pleaseEnter')}
                                        onChange={e => onSetShared(field.key, e.target.value)}
                                        data-testid={`copy-asset-shared-${field.key}`}
                                    />
                                </FormField>
                            </GridItem>
                        )
                    })}
                    <GridItem className="copy-asset__field">
                        {ownValuesNote(overrideCount(state, "availabilityDate"))}
                        <FormField label={t('asset.expectedToBeAvailableOn')}>
                            <InputDateTime
                                type="date"
                                date={state.shared.availabilityDate}
                                utc
                                onDateChange={date => onSetShared("availabilityDate", date)}
                            />
                        </FormField>
                    </GridItem>
                </Grid>
            }
        </div>
    )
}

export default CopyAssetSharedDefaults
